import { useCallback } from 'react'

import styled from '@emotion/styled'
import type { TFunction } from 'i18next'

import { ArcadeButtonLink, spacing } from '@cash-design-system/react'
import type { LinkAdapterProps } from '@cash-web/support/ui-components'

interface MoreResultsButtonProps<T> extends LinkAdapterProps<T> {
  query: string
  t: TFunction
}

const ButtonContainer = styled.div`
  margin: ${spacing.s} auto ${spacing.xl};
`

// A region (`us`, `gb`) or locale (`es`, `en-us`, `en-gb`) path segment. Used to
// distinguish them from page segments like `search` or article slugs
// (`3113-bill-pay`).
const REGION_OR_LOCALE_SEGMENT = /^[a-z]{2}(-[a-z]{2,3})?$/i

/**
 * Rebuild the search path from the current URL, preserving the region and locale
 * segments (e.g. `/help/us/es/search`). Without this the link falls back to the
 * base path only (`/help/search`), which resolves to the default English page.
 *
 * The order of the region and locale segments differs by environment (production
 * serves `/help/us/es/search`, local dev serves `/help/es-US/us/search`), so both
 * leading segments are preserved as-is rather than assuming a fixed order.
 */
const buildLocalizedSearchRoute = (pathname: string, encodedQuery: string): string => {
  const [basePath, ...rest] = pathname.split('/').filter(Boolean)

  const segments: string[] = []
  if (basePath) segments.push(basePath) // e.g. `help` or `support`
  // Preserve up to two leading region/locale segments before the page segment.
  for (const segment of rest.slice(0, 2)) {
    if (!REGION_OR_LOCALE_SEGMENT.test(segment)) break
    segments.push(segment)
  }

  const prefix = segments.length ? `/${segments.join('/')}` : ''
  return `${prefix}/search#${encodedQuery}`
}

const MoreResultsButton = <T,>({ query, t }: MoreResultsButtonProps<T>) => {
  const encodedQuery = encodeURIComponent(query)
  const label = t('More results', { context: 'search view more results button' })

  const fullRoute = buildLocalizedSearchRoute(window.location.pathname, encodedQuery)

  const handleClick = useCallback(
    (event: { preventDefault: () => void }) => {
      event.preventDefault()

      // push new route
      window.history.pushState({}, '', fullRoute)

      // reload page
      window.location.reload()
    },
    [fullRoute]
  )

  return (
    <ButtonContainer>
      <ArcadeButtonLink aria-label={label} href={fullRoute} onClick={handleClick} prominence="standard">
        {label}
      </ArcadeButtonLink>
    </ButtonContainer>
  )
}

export default MoreResultsButton
